/**
 * The coordinator API: every operation, its input and output schema, the error model,
 * and the event subscription. Types only. `mock-coordinator.ts` is the reference behaviour;
 * the real coordinator (a Durable Object) implements the same interface and passes the same tests.
 *
 * Every operation takes one input object and returns a Promise, so it maps 1:1 onto an RPC call,
 * an MCP tool or a Durable Object method.
 */
import { z } from "zod";
import { ClaimModeSchema, ClaimStatusSchema, IntentClaimSchema, type IntentClaim } from "./claim.ts";
import { PlatformEventSchema, PlatformEventTypeSchema, type PlatformEvent } from "./events.ts";
import { DiffSummarySchema, type DiffSummary } from "./git.ts";
import { LandRequestSchema, MigrationLaneSchema, type LandRequest, type MigrationLane } from "./land.ts";
import { BROADCAST, MessageSchema, MessageTypeSchema, messageShapeIssues, type Message } from "./message.ts";
import { OverlapCandidateSchema, OverlapKindSchema, OverlapSchema, OverlapStatusSchema, type Overlap } from "./overlap.ts";
import { WorkPackageDraftSchema, WorkPackageSchema, WorkPlanDraftSchema, WorkPlanSchema, type WorkPackage, type WorkPlan } from "./plan.ts";
import { CommitShaSchema, IdSchema, PrincipalSchema, ScopeSchema, WpIdSchema } from "./primitives.ts";
import { ProposalSchema, ResolutionInputSchema, ResolutionSchema, type Resolution } from "./resolution.ts";
import { AgentRunSchema, type AgentRun } from "./run.ts";
import { SessionSchema, SessionStatusSchema, type Session } from "./session.ts";
import { DeployRecordSchema, type DeployRecord } from "./stack.ts";
import { VerdictSchema, type Verdict } from "./verdict.ts";

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Coordinator timing defaults. A real coordinator should use the same numbers. */
export const COORDINATOR_DEFAULTS = {
  /** A session with no heartbeat for this long is `idle`. */
  idleAfterMs: 2 * 60_000,
  /** A session with no heartbeat for this long is `expired` and its claims expire. */
  sessionTtlMs: 10 * 60_000,
  /** Default claim lease. Each heartbeat renews the session's claims for this long. */
  claimTtlMs: 30 * 60_000,
  /** Longest claim lease a caller may ask for. */
  maxClaimTtlMs: 4 * 60 * 60_000,
  /** Longest a readMessages call may wait for new messages. */
  maxWaitMs: 30_000,
} as const;

// ---------------------------------------------------------------------------
// Inputs and outputs
// ---------------------------------------------------------------------------

/** Input to `registerSession`. */
export const RegisterSessionInputSchema = SessionSchema.pick({ agentName: true, wpId: true, forkRepo: true, forkRemote: true, vendor: true, accountId: true });
/** See {@link RegisterSessionInputSchema}. */
export type RegisterSessionInput = z.infer<typeof RegisterSessionInputSchema>;

/** Input to `heartbeat`. Send one at least every minute. */
export const HeartbeatInputSchema = z.object({
  sessionId: IdSchema,
  /** Optional progress note for the dashboard. Omit to keep the previous note. */
  note: z.string().max(500).optional(),
});
/** See {@link HeartbeatInputSchema}. */
export type HeartbeatInput = z.infer<typeof HeartbeatInputSchema>;

/** Input to `declareClaim`. */
export const DeclareClaimInputSchema = z.object({
  sessionId: IdSchema,
  scope: ScopeSchema,
  rationale: z.string().min(1).max(2000),
  mode: ClaimModeSchema,
  /** Lease length. Defaults to `COORDINATOR_DEFAULTS.claimTtlMs`, capped at `maxClaimTtlMs`. */
  ttlMs: z.number().int().positive().optional(),
});
/** See {@link DeclareClaimInputSchema}. */
export type DeclareClaimInput = z.infer<typeof DeclareClaimInputSchema>;

/** Output of `declareClaim`. */
export const DeclareClaimResultSchema = z.object({
  /** The recorded claim. Status `queued` if it needs the migration lane and the lane is busy. */
  claim: IntentClaimSchema,
  /** Overlaps this claim raised, if any. */
  overlaps: z.array(OverlapSchema),
  /** True when any of those overlaps is `block`: stop and negotiate before writing code. */
  blocked: z.boolean(),
});
/** See {@link DeclareClaimResultSchema}. */
export type DeclareClaimResult = z.infer<typeof DeclareClaimResultSchema>;

/** Input to `releaseClaim`. */
export const ReleaseClaimInputSchema = z.object({ sessionId: IdSchema, claimId: IdSchema });
/** See {@link ReleaseClaimInputSchema}. */
export type ReleaseClaimInput = z.infer<typeof ReleaseClaimInputSchema>;

/** Input to `checkOverlap`: the claim you are thinking of declaring. Nothing is recorded. */
export const CheckOverlapInputSchema = z.object({ sessionId: IdSchema, scope: ScopeSchema, mode: ClaimModeSchema });
/** See {@link CheckOverlapInputSchema}. */
export type CheckOverlapInput = z.infer<typeof CheckOverlapInputSchema>;

/** Output of `checkOverlap`. */
export const CheckOverlapResultSchema = z.object({
  candidates: z.array(OverlapCandidateSchema),
  /** True when declaring would raise at least one `block` overlap. */
  wouldBlock: z.boolean(),
  /** True when declaring would queue for the migration lane. */
  wouldQueue: z.boolean(),
});
/** See {@link CheckOverlapResultSchema}. */
export type CheckOverlapResult = z.infer<typeof CheckOverlapResultSchema>;

/** Input to `listSessions`. Defaults to live sessions (`active` and `idle`). */
export const ListSessionsInputSchema = z.object({
  statuses: z.array(SessionStatusSchema).optional(),
  wpId: WpIdSchema.optional(),
});
/** See {@link ListSessionsInputSchema}. */
export type ListSessionsInput = z.infer<typeof ListSessionsInputSchema>;

/** Input to `listClaims`. Defaults to live claims (`active` and `queued`). */
export const ListClaimsInputSchema = z.object({
  sessionId: IdSchema.optional(),
  wpId: WpIdSchema.optional(),
  statuses: z.array(ClaimStatusSchema).optional(),
});
/** See {@link ListClaimsInputSchema}. */
export type ListClaimsInput = z.infer<typeof ListClaimsInputSchema>;

/** Input to `listOverlaps`. Defaults to every overlap. `sessionId` matches either side. */
export const ListOverlapsInputSchema = z.object({
  sessionId: IdSchema.optional(),
  statuses: z.array(OverlapStatusSchema).optional(),
  kinds: z.array(OverlapKindSchema).optional(),
});
/** See {@link ListOverlapsInputSchema}. */
export type ListOverlapsInput = z.infer<typeof ListOverlapsInputSchema>;

/** Input to `postMessage`. The same per-type rules as {@link MessageSchema} apply. */
export const PostMessageInputSchema = z
  .object({
    from: PrincipalSchema,
    to: z.union([z.literal(BROADCAST), IdSchema]),
    overlapId: IdSchema.optional(),
    type: MessageTypeSchema,
    body: z.string().min(1).max(8000),
    inReplyTo: IdSchema.optional(),
    proposal: ProposalSchema.optional(),
  })
  .superRefine((m, ctx) => {
    for (const message of messageShapeIssues(m)) ctx.addIssue({ code: "custom", message });
  });
/** See {@link PostMessageInputSchema}. */
export type PostMessageInput = z.infer<typeof PostMessageInputSchema>;

/**
 * Input to `readMessages`. Returns messages addressed to `sessionId` or broadcast, oldest first.
 * With `overlapId`, returns that overlap's whole thread instead (both directions).
 */
export const ReadMessagesInputSchema = z.object({
  sessionId: IdSchema,
  /** Only messages after this message id (exclusive). Pass the last id you saw. */
  after: IdSchema.optional(),
  overlapId: IdSchema.optional(),
  /** If nothing matches yet, wait up to this long for a new message. Capped at `maxWaitMs`. */
  waitMs: z.number().int().min(0).optional(),
  /** Maximum messages to return. Default 100. */
  limit: z.number().int().positive().max(500).optional(),
});
/** See {@link ReadMessagesInputSchema}. */
export type ReadMessagesInput = z.infer<typeof ReadMessagesInputSchema>;

/** Input to `recordResolution`: a {@link Resolution} without the coordinator-assigned `id` and `ts`. */
export const RecordResolutionInputSchema = ResolutionInputSchema;
/** See {@link RecordResolutionInputSchema}. */
export type RecordResolutionInput = z.infer<typeof RecordResolutionInputSchema>;

/** Input to `listResolutions`. */
export const ListResolutionsInputSchema = z.object({ overlapId: IdSchema.optional() });
/** See {@link ListResolutionsInputSchema}. */
export type ListResolutionsInput = z.infer<typeof ListResolutionsInputSchema>;

/** Input to `submitPlan`. Resubmitting a draft with the same specId replaces it. */
export const SubmitPlanInputSchema = WorkPlanDraftSchema;
/** See {@link SubmitPlanInputSchema}. */
export type SubmitPlanInput = z.infer<typeof SubmitPlanInputSchema>;

/** Input to `approvePlan`. Only a human may approve. Blocking questions must be answered (here or before). */
export const ApprovePlanInputSchema = z.object({
  specId: z.string().min(1),
  approvedBy: PrincipalSchema,
  /** Answers to plan questions, by question id. */
  answers: z.record(z.string(), z.string().min(1)).optional(),
});
/** See {@link ApprovePlanInputSchema}. */
export type ApprovePlanInput = z.infer<typeof ApprovePlanInputSchema>;

/**
 * Input to `amendPlan` (contracts-v3): change an approved plan without a new approval. Service only, and
 * `amendedBy` must be a `system:` principal: the run's commander re-planning or closing a gap, inside the
 * authority the human's approval of the plan gave it. Added packages are `approved` at once.
 */
export const AmendPlanInputSchema = z
  .object({
    specId: z.string().min(1),
    amendedBy: PrincipalSchema,
    reason: z.string().min(1).max(2000),
    /** New packages. They may depend on packages already in the plan, or on each other. */
    add: z.array(WorkPackageDraftSchema).optional(),
    /** Packages to retire, and the packages (existing or added here) that replace each. Never a landed one. */
    supersede: z.array(z.object({ wpId: WpIdSchema, replacedBy: z.array(WpIdSchema) })).optional(),
    /** Dependencies to add to packages that have not landed, for example the integration sweep waiting on a gap package. */
    rewire: z.array(z.object({ wpId: WpIdSchema, addDependsOn: z.array(WpIdSchema).min(1) })).optional(),
  })
  .refine((a) => (a.add?.length ?? 0) + (a.supersede?.length ?? 0) + (a.rewire?.length ?? 0) > 0, { message: "an amendment must change something" });
/** See {@link AmendPlanInputSchema}. */
export type AmendPlanInput = z.infer<typeof AmendPlanInputSchema>;

/** Input to `getPlan`. */
export const GetPlanInputSchema = z.object({ specId: z.string().min(1) });
/** See {@link GetPlanInputSchema}. */
export type GetPlanInput = z.infer<typeof GetPlanInputSchema>;

/** Input to `getWorkPackage`. */
export const GetWorkPackageInputSchema = z.object({ wpId: WpIdSchema });
/** See {@link GetWorkPackageInputSchema}. */
export type GetWorkPackageInput = z.infer<typeof GetWorkPackageInputSchema>;

/** Input to `dispatchPackage`: record that a fork and brief exist for an approved package. */
export const DispatchPackageInputSchema = z.object({ wpId: WpIdSchema, forkRepo: z.string().min(1) });
/** See {@link DispatchPackageInputSchema}. */
export type DispatchPackageInput = z.infer<typeof DispatchPackageInputSchema>;

/** Output of `ingestDiffSummary`. */
export const IngestDiffResultSchema = z.object({
  diff: DiffSummarySchema,
  /** New `diff` overlaps this push raised. */
  overlaps: z.array(OverlapSchema),
});
/** See {@link IngestDiffResultSchema}. */
export type IngestDiffResult = z.infer<typeof IngestDiffResultSchema>;

/** Output of `ingestVerdict`. */
export const IngestVerdictResultSchema = z.object({
  verdict: VerdictSchema,
  /** The land request for this commit, if one was waiting on the verdict, after re-evaluation. */
  land: LandRequestSchema.nullable(),
});
/** See {@link IngestVerdictResultSchema}. */
export type IngestVerdictResult = z.infer<typeof IngestVerdictResultSchema>;

/** Input to `requestLand`. */
export const RequestLandInputSchema = z.object({ sessionId: IdSchema, commit: CommitShaSchema });
/** See {@link RequestLandInputSchema}. */
export type RequestLandInput = z.infer<typeof RequestLandInputSchema>;

/** Input to `reportLandResult`, sent by the merge service after it tries to merge an approved land. */
export const ReportLandResultInputSchema = z
  .object({
    landId: IdSchema,
    outcome: z.enum(["merged", "conflict", "failed"]),
    /** Required when merged. */
    mergeCommit: CommitShaSchema.optional(),
    /** Conflicting paths. Required (non-empty) on conflict. */
    conflicts: z.array(z.string().min(1)).optional(),
    /** Why it failed. Required when the outcome is `failed`. */
    reason: z.string().min(1).max(2000).optional(),
  })
  .refine((r) => r.outcome !== "merged" || r.mergeCommit !== undefined, { message: "merged needs mergeCommit", path: ["mergeCommit"] })
  .refine((r) => r.outcome !== "conflict" || (r.conflicts?.length ?? 0) > 0, { message: "conflict needs conflicts", path: ["conflicts"] })
  .refine((r) => r.outcome !== "failed" || (r.reason?.length ?? 0) > 0, { message: "failed needs a reason", path: ["reason"] });
/** See {@link ReportLandResultInputSchema}. */
export type ReportLandResultInput = z.infer<typeof ReportLandResultInputSchema>;

/** Input to `approvePromotion`. Only a human may approve, and only a succeeded preview. */
export const ApprovePromotionInputSchema = z.object({ deployId: IdSchema, approvedBy: PrincipalSchema });
/** See {@link ApprovePromotionInputSchema}. */
export type ApprovePromotionInput = z.infer<typeof ApprovePromotionInputSchema>;

/** Output of `snapshot`: the whole current state, for a dashboard's first paint or a reconnect. */
export const CoordinatorSnapshotSchema = z.object({
  sessions: z.array(SessionSchema),
  claims: z.array(IntentClaimSchema),
  overlaps: z.array(OverlapSchema),
  messages: z.array(MessageSchema),
  resolutions: z.array(ResolutionSchema),
  plans: z.array(WorkPlanSchema),
  verdicts: z.array(VerdictSchema),
  lands: z.array(LandRequestSchema),
  deploys: z.array(DeployRecordSchema),
  /** Agent runs, as their runner last reported them. Empty from a coordinator that predates runs. */
  runs: z.array(AgentRunSchema).default([]),
  migrationLane: MigrationLaneSchema,
  /** Seq of the last event included in this state. Subscribe with `since: lastSeq` to continue live. */
  lastSeq: z.number().int().min(0),
});
/** See {@link CoordinatorSnapshotSchema}. */
export type CoordinatorSnapshot = z.infer<typeof CoordinatorSnapshotSchema>;

const EmptyInputSchema = z.object({});

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Why an operation failed.
 * - `invalid_input`: the input failed its schema. `details` has the zod issues.
 * - `not_found`: a referenced id does not exist.
 * - `forbidden`: the caller may not do this (not a party to the overlap, not a human, not the owner).
 * - `conflict`: the state already moved on (already resolved, already approved, duplicate).
 * - `precondition_failed`: the state does not allow it yet (session expired, plan not approved, package not dispatched).
 * - `internal`: an unexpected failure inside the coordinator or transport. Safe to retry.
 * - `unauthenticated` (contracts-v2): no credential, or it is unknown, revoked or expired. Sign in again.
 */
export const CoordinatorErrorCodeSchema = z.enum([
  "invalid_input",
  "unauthenticated",
  "not_found",
  "forbidden",
  "conflict",
  "precondition_failed",
  "internal",
]);
/** See {@link CoordinatorErrorCodeSchema}. */
export type CoordinatorErrorCode = z.infer<typeof CoordinatorErrorCodeSchema>;

/** HTTP status each error code maps to. */
export const ERROR_HTTP_STATUS: Record<CoordinatorErrorCode, number> = {
  invalid_input: 400,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  precondition_failed: 412,
  internal: 500,
};

/** Wire form of an error. */
export const CoordinatorErrorBodySchema = z.object({
  code: CoordinatorErrorCodeSchema,
  message: z.string(),
  details: z.unknown().optional(),
});
/** See {@link CoordinatorErrorBodySchema}. */
export type CoordinatorErrorBody = z.infer<typeof CoordinatorErrorBodySchema>;

/** The error every coordinator operation throws (or rejects with). */
export class CoordinatorError extends Error {
  readonly code: CoordinatorErrorCode;
  readonly details: unknown;
  constructor(code: CoordinatorErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = "CoordinatorError";
    this.code = code;
    this.details = details;
  }
  /** Wire form, for transports. */
  toJSON(): CoordinatorErrorBody {
    return this.details === undefined
      ? { code: this.code, message: this.message }
      : { code: this.code, message: this.message, details: this.details };
  }
}

/** True when a value is a {@link CoordinatorError} (also across bundles, by name and code). */
export function isCoordinatorError(e: unknown): e is CoordinatorError {
  return e instanceof CoordinatorError || (e instanceof Error && e.name === "CoordinatorError" && "code" in e);
}

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

/** Options for `subscribe`. */
export const SubscribeOptionsSchema = z.object({
  /** Replay events with `seq` greater than this first, then continue live. Omit for live only. 0 replays everything. */
  since: z.number().int().min(0).optional(),
  /** Only these event types. Omit for all. */
  types: z.array(PlatformEventTypeSchema).optional(),
});
/** See {@link SubscribeOptionsSchema}. */
export type SubscribeOptions = z.infer<typeof SubscribeOptionsSchema>;

/** Handle returned by `subscribe`. */
export interface Subscription {
  /** Stop receiving events. Safe to call twice. */
  close(): void;
}

/** Path of the coordinator's WebSocket event stream. */
export const EVENTS_WS_PATH = "/v1/events";

/**
 * Response header that carries a new session's token, sent once, by `registerSession`. Every later call made
 * as that session sends it as `Authorization: Bearer <token>`. Keep it server-side (for example in the MCP server).
 */
export const SESSION_TOKEN_HEADER = "x-botallion-session-token";

/** `POST` here, authenticated, for a single-use ticket (60 s) to open the event WebSocket with `?ticket=<ticket>`. */
export const EVENTS_TICKET_PATH = "/v1/events/ticket";

/**
 * Frames on the event WebSocket. The client sends `subscribe` once after connecting
 * (and may send `ping`); the server answers `ready`, then streams `event` frames.
 */
export const ClientFrameSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("subscribe"), since: z.number().int().min(0).optional(), types: z.array(PlatformEventTypeSchema).optional() }),
  z.object({ type: z.literal("ping") }),
]);
/** See {@link ClientFrameSchema}. */
export type ClientFrame = z.infer<typeof ClientFrameSchema>;

/** Server-to-client frames, see {@link ClientFrameSchema}. */
export const ServerFrameSchema = z.discriminatedUnion("type", [
  /** Subscription accepted. `lastSeq` is the newest event at that moment. */
  z.object({ type: z.literal("ready"), lastSeq: z.number().int().min(0) }),
  z.object({ type: z.literal("event"), event: PlatformEventSchema }),
  z.object({ type: z.literal("error"), error: CoordinatorErrorBodySchema }),
  z.object({ type: z.literal("pong") }),
]);
/** See {@link ServerFrameSchema}. */
export type ServerFrame = z.infer<typeof ServerFrameSchema>;

// ---------------------------------------------------------------------------
// The interface
// ---------------------------------------------------------------------------

/**
 * The coordinator: single source of truth for sessions, claims, overlaps, messages, resolutions,
 * plans, verdicts, lands, deploys and agent runs for one repository. Every state change emits a {@link PlatformEvent}.
 *
 * All methods validate their input and reject with a {@link CoordinatorError} on failure.
 */
export interface Coordinator {
  // Sessions ---------------------------------------------------------------

  /**
   * Register an agent session for a work package. If the package exists it must be dispatched
   * (or in progress, or failed); registering moves a dispatched package to `in_progress`.
   */
  registerSession(input: RegisterSessionInput): Promise<Session>;
  /** Keep a session alive, renew its claims' leases, and optionally update its progress note. */
  heartbeat(input: HeartbeatInput): Promise<Session>;
  /** List sessions. Defaults to live ones (`active`, `idle`). */
  listSessions(input?: ListSessionsInput): Promise<Session[]>;

  // Claims and overlaps ----------------------------------------------------

  /**
   * Declare intent before editing. Detects overlaps against every other live claim and returns them.
   * If `blocked` is true, negotiate before writing code. Migration claims queue if the lane is busy.
   */
  declareClaim(input: DeclareClaimInput): Promise<DeclareClaimResult>;
  /** Release a claim you hold. Unresolved overlaps on it become `released`; the migration lane advances. */
  releaseClaim(input: ReleaseClaimInput): Promise<IntentClaim>;
  /** Dry run of `declareClaim`: what would collide. Records nothing. */
  checkOverlap(input: CheckOverlapInput): Promise<CheckOverlapResult>;
  /** List claims. Defaults to live ones (`active`, `queued`). */
  listClaims(input?: ListClaimsInput): Promise<IntentClaim[]>;
  /** List overlaps, newest last. */
  listOverlaps(input?: ListOverlapsInput): Promise<Overlap[]>;
  /** The migration lane: who holds it and who is queued. */
  getMigrationLane(): Promise<MigrationLane>;

  // Negotiation ------------------------------------------------------------

  /**
   * Post a message. Protocol effects: a proposal or counter moves its overlap to `negotiating`;
   * `accept` records the accepted proposal as a resolution; `yield` records a sequence with the
   * other side first.
   */
  postMessage(input: PostMessageInput): Promise<Message>;
  /** Read your inbox (direct and broadcast), or one overlap's thread. Can wait briefly for new messages. */
  readMessages(input: ReadMessagesInput): Promise<Message[]>;
  /**
   * Record a resolution directly (escalation, timeouts, rules, human decisions). Agreements between
   * agents normally go through `accept` instead. The coordinator enforces the terms.
   */
  recordResolution(input: RecordResolutionInput): Promise<Resolution>;
  /** List resolutions, oldest first. */
  listResolutions(input?: ListResolutionsInput): Promise<Resolution[]>;

  // Plans and packages -----------------------------------------------------

  /** Submit a plan as a draft. Resubmitting a draft with the same specId replaces it. */
  submitPlan(input: SubmitPlanInput): Promise<WorkPlan>;
  /** Approve a plan (human only). Every package moves to `approved`. */
  approvePlan(input: ApprovePlanInput): Promise<WorkPlan>;
  /**
   * Amend an approved plan (contracts-v3, service only, a `system:` principal): add packages, supersede packages
   * that have not landed, add dependencies. Dependents of a superseded package depend on its replacements.
   */
  amendPlan(input: AmendPlanInput): Promise<WorkPlan>;
  /** Read a plan. */
  getPlan(input: GetPlanInput): Promise<WorkPlan>;
  /** Read one work package. */
  getWorkPackage(input: GetWorkPackageInput): Promise<WorkPackage>;
  /** Mark an approved package dispatched, with its fork. */
  dispatchPackage(input: DispatchPackageInput): Promise<WorkPackage>;

  // Diffs, verdicts and landing --------------------------------------------

  /** Ingest a pushed diff. Raises `diff` overlaps against other live sessions' claims and diffs. */
  ingestDiffSummary(input: DiffSummary): Promise<IngestDiffResult>;
  /** Ingest a verdict. Re-evaluates any land request waiting on it. */
  ingestVerdict(input: Verdict): Promise<IngestVerdictResult>;
  /**
   * Ask to land a commit. Gates: a passing verdict for that commit, no unresolved blocking overlap,
   * sequenced predecessors landed, dependencies landed, migration lane held (if migrating).
   * Returns `approved`, `pending` (waiting on a gate, re-checked automatically) or `rejected`.
   */
  requestLand(input: RequestLandInput): Promise<LandRequest>;
  /** Report the merge result of an approved land. `merged` lands the package and session. */
  reportLandResult(input: ReportLandResultInput): Promise<LandRequest>;

  // Deploys ----------------------------------------------------------------

  /** Create or update a deploy record (upsert by id). */
  recordDeploy(input: DeployRecord): Promise<DeployRecord>;
  /** Approve promoting a succeeded preview to production (human only). */
  approvePromotion(input: ApprovePromotionInput): Promise<DeployRecord>;

  // Runs -------------------------------------------------------------------

  /** Create or update an agent run (upsert by id), as the runner sees it. Service only. */
  reportRun(input: AgentRun): Promise<AgentRun>;

  // Observing --------------------------------------------------------------

  /** The whole current state. */
  snapshot(): Promise<CoordinatorSnapshot>;
  /**
   * Receive events. With `since`, events after that seq are replayed first, in order, before live ones.
   * Over the network this is the WebSocket at {@link EVENTS_WS_PATH}.
   */
  subscribe(options: SubscribeOptions, listener: (event: PlatformEvent) => void): Subscription;
}

// ---------------------------------------------------------------------------
// Operation map (for transports: RPC, MCP, Durable Object)
// ---------------------------------------------------------------------------

/**
 * Every request/response operation with its input and output schema. Transports use this to
 * validate and route generically. `subscribe` is not here: it is a stream, see {@link EVENTS_WS_PATH}.
 * Operations whose input is optional accept `{}`.
 */
export const coordinatorOperations = {
  registerSession: { input: RegisterSessionInputSchema, output: SessionSchema },
  heartbeat: { input: HeartbeatInputSchema, output: SessionSchema },
  listSessions: { input: ListSessionsInputSchema, output: z.array(SessionSchema) },
  declareClaim: { input: DeclareClaimInputSchema, output: DeclareClaimResultSchema },
  releaseClaim: { input: ReleaseClaimInputSchema, output: IntentClaimSchema },
  checkOverlap: { input: CheckOverlapInputSchema, output: CheckOverlapResultSchema },
  listClaims: { input: ListClaimsInputSchema, output: z.array(IntentClaimSchema) },
  listOverlaps: { input: ListOverlapsInputSchema, output: z.array(OverlapSchema) },
  getMigrationLane: { input: EmptyInputSchema, output: MigrationLaneSchema },
  postMessage: { input: PostMessageInputSchema, output: MessageSchema },
  readMessages: { input: ReadMessagesInputSchema, output: z.array(MessageSchema) },
  recordResolution: { input: RecordResolutionInputSchema, output: ResolutionSchema },
  listResolutions: { input: ListResolutionsInputSchema, output: z.array(ResolutionSchema) },
  submitPlan: { input: SubmitPlanInputSchema, output: WorkPlanSchema },
  approvePlan: { input: ApprovePlanInputSchema, output: WorkPlanSchema },
  amendPlan: { input: AmendPlanInputSchema, output: WorkPlanSchema },
  getPlan: { input: GetPlanInputSchema, output: WorkPlanSchema },
  getWorkPackage: { input: GetWorkPackageInputSchema, output: WorkPackageSchema },
  dispatchPackage: { input: DispatchPackageInputSchema, output: WorkPackageSchema },
  ingestDiffSummary: { input: DiffSummarySchema, output: IngestDiffResultSchema },
  ingestVerdict: { input: VerdictSchema, output: IngestVerdictResultSchema },
  requestLand: { input: RequestLandInputSchema, output: LandRequestSchema },
  reportLandResult: { input: ReportLandResultInputSchema, output: LandRequestSchema },
  recordDeploy: { input: DeployRecordSchema, output: DeployRecordSchema },
  approvePromotion: { input: ApprovePromotionInputSchema, output: DeployRecordSchema },
  reportRun: { input: AgentRunSchema, output: AgentRunSchema },
  snapshot: { input: EmptyInputSchema, output: CoordinatorSnapshotSchema },
} as const satisfies Record<Exclude<keyof Coordinator, "subscribe">, { input: z.ZodType; output: z.ZodType }>;

/** Name of a request/response operation. */
export type CoordinatorOperation = keyof typeof coordinatorOperations;
/** Every operation name, in declaration order. */
export const COORDINATOR_OPERATIONS = Object.keys(coordinatorOperations) as CoordinatorOperation[];

/** Path of the RPC endpoint for an operation: `POST /v1/rpc/<operation>` with the input as the JSON body. */
export function rpcPath(operation: CoordinatorOperation): string {
  return `/v1/rpc/${operation}`;
}

/** JSON body of every RPC response. */
export const RpcResponseSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), result: z.unknown() }),
  z.object({ ok: z.literal(false), error: CoordinatorErrorBodySchema }),
]);
/** See {@link RpcResponseSchema}. */
export type RpcResponse = z.infer<typeof RpcResponseSchema>;
